import React from "react";
import {
  adminCreateTimecard,
  adminUpdateTimecard,
  approveTimecard,
  createAdminContractor,
  createHospital,
  fetchAdminContractors,
  fetchAdminTimecards,
  updateAdminContractor,
  updateHospital
} from "../../api/admin";
import { fetchHospitalOptions, normalizeHospitalOption } from "../../api/hospitals";
import { downloadHospitalCombinedExport, downloadTimecardExport, fetchResubmissions, fetchTimecard } from "../../api/timecards";
import AppHeader from "../common/AppHeader";
import Field from "../common/Field";
import SectionHeading from "../common/SectionHeading";
import Toast from "../common/Toast";
import TimecardEntryArea from "../timecard/TimecardEntryArea";
import AdminTabs from "./AdminTabs";
import { useToast } from "../../hooks/useToast";
import {
  addWeeks,
  applyCallBackRowChange,
  applyRegularRowChange,
  buildTimecardPayload,
  calculateCallBackTotalHours,
  calculateCallTotalHours,
  createEmptyRows,
  formatDisplayDate,
  formatLocalDate,
  formatWeekRange,
  getDefaultWeekRange,
  getNetCallHours,
  isDepartmentValidForHospital,
  mapTimecardToFormState
} from "../../utils/timecard";
import {
  formatStatusLabel,
  parseLocalDate,
  formatLastLogin,
  buildReviewItems,
  buildStatusCounts,
  formatRelativeTime,
  formatHospitalSummary
} from "../../utils/adminDashboard";

function HospitalSummaryCell({ summary, hospitalNames }) {
  const [tooltipPos, setTooltipPos] = React.useState(null);
  const cellRef = React.useRef(null);
  const showTimer = React.useRef(null);
  const hideTimer = React.useRef(null);

  const hasMultiple = hospitalNames.length > 1;

  function showTooltip() {
    if (!hasMultiple) return;
    clearTimeout(hideTimer.current);
    showTimer.current = setTimeout(() => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        setTooltipPos({ top: rect.bottom + 4, left: rect.left });
      }
    }, 120);
  }

  function hideTooltip() {
    clearTimeout(showTimer.current);
    hideTimer.current = setTimeout(() => setTooltipPos(null), 80);
  }

  React.useEffect(() => () => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
  }, []);

  return (
    <div
      ref={cellRef}
      className={`rq-col-hospital${hasMultiple ? " rq-col-hospital--expandable" : ""}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {summary ?? <span className="rq-no-hospital">No hospitals</span>}
      {tooltipPos ? (
        <div
          className="hospital-tooltip"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          {hospitalNames.map((name) => (
            <div key={name} className="hospital-tooltip-item">{name}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}


function DepartmentNameEditor({
  inputId,
  value,
  onValueChange,
  departments,
  onAddDepartment,
  onRemoveDepartment,
  onRenameDepartment = () => {},
  disabled = false,
  emptyMessage = "No departments added yet."
}) {
  const [editingChip, setEditingChip] = React.useState(null);
  const [editingValue, setEditingValue] = React.useState("");
  const suppressBlurRef = React.useRef(false);

  const handleAddKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onAddDepartment();
    }
  };

  const startEdit = (name) => {
    setEditingChip(name);
    setEditingValue(name);
  };

  const commitEdit = () => {
    const trimmed = editingValue.trim();
    if (trimmed && editingChip && trimmed.toLowerCase() !== editingChip.toLowerCase()) {
      onRenameDepartment(editingChip, trimmed);
    }
    setEditingChip(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingChip(null);
    setEditingValue("");
  };

  const handleEditKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitEdit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      suppressBlurRef.current = true;
      cancelEdit();
    }
  };

  const handleEditBlur = () => {
    if (suppressBlurRef.current) {
      suppressBlurRef.current = false;
      return;
    }
    commitEdit();
  };

  return (
    <div className="department-editor">
      <div className="dept-add-row">
        <input
          id={inputId}
          type="text"
          className="dept-add-input"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={handleAddKeyDown}
          disabled={disabled}
          placeholder="Department name"
          aria-label="New department name"
        />
        <button
          type="button"
          className="dept-editor-add-btn"
          onClick={onAddDepartment}
          disabled={disabled || !value.trim()}
        >
          Add
        </button>
      </div>

      {departments.length > 0 ? (
        <ul className="dept-list" aria-label="Configured departments">
          {departments.map((departmentName) => (
            <li key={departmentName} className={`dept-list-item${editingChip === departmentName ? " dept-list-item--editing" : ""}`}>
              {editingChip === departmentName ? (
                <input
                  type="text"
                  className="dept-list-input"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  onBlur={handleEditBlur}
                  autoFocus
                  aria-label={`Rename ${departmentName}`}
                />
              ) : (
                <>
                  <span className="dept-list-name">{departmentName}</span>
                  <div className="dept-list-actions">
                    <button
                      type="button"
                      className="dept-list-btn"
                      aria-label={`Rename ${departmentName}`}
                      onClick={() => startEdit(departmentName)}
                      disabled={disabled}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="dept-list-btn dept-list-btn--remove"
                      aria-label={`Remove ${departmentName}`}
                      onClick={() => onRemoveDepartment(departmentName)}
                      disabled={disabled}
                    >
                      Remove
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="department-editor-empty">{emptyMessage}</p>
      )}
    </div>
  );
}

export default function AdminDashboard({ session, onLogout, logoUrl }) {
  const [activeTab, setActiveTab] = React.useState("regular");
  const [activeAdminTab, setActiveAdminTab] = React.useState("review");
  const [currentWeekOffset, setCurrentWeekOffset] = React.useState(0);
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [hospitalOptions, setHospitalOptions] = React.useState([]);
  const [contractors, setContractors] = React.useState([]);
  const [timecards, setTimecards] = React.useState([]);
  const [selectedContractorId, setSelectedContractorId] = React.useState(0);
  const [selectedTimecard, setSelectedTimecard] = React.useState(null);
  const [resubmissions, setResubmissions] = React.useState([]);
  const [regularRows, setRegularRows] = React.useState(() => createEmptyRows().regularRows);
  const [callRows, setCallRows] = React.useState(() => createEmptyRows().callRows);
  const [callbackRows, setCallbackRows] = React.useState(() => createEmptyRows().callbackRows);
  const [weekStartDate, setWeekStartDate] = React.useState(() => getDefaultWeekRange()[0]);
  const [reviewLoadError, setReviewLoadError] = React.useState("");
  const { toastMessage, toastVisible, showToast, dismissToast } = useToast();
  const [dashboardLoading, setDashboardLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [approveLoading, setApproveLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [hospitalExportLoading, setHospitalExportLoading] = React.useState({});
  const [isEditing, setIsEditing] = React.useState(false);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [newHospitalName, setNewHospitalName] = React.useState("");
  const [hospitalSearch, setHospitalSearch] = React.useState("");
  const [hospitalError, setHospitalError] = React.useState("");
  const [isCreatingHospital, setIsCreatingHospital] = React.useState(false);
  const [newEmployee, setNewEmployee] = React.useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    isActive: true
  });
  const [employeeError, setEmployeeError] = React.useState("");
  const [isCreatingEmployee, setIsCreatingEmployee] = React.useState(false);
  const [openMenuHospitalId, setOpenMenuHospitalId] = React.useState(null);
  const [editingHospital, setEditingHospital] = React.useState(null);
  const [editHospitalName, setEditHospitalName] = React.useState("");
  const [editHospitalDepartmentName, setEditHospitalDepartmentName] = React.useState("");
  const [editHospitalDepartments, setEditHospitalDepartments] = React.useState([]);
  const [editHospitalExactTime, setEditHospitalExactTime] = React.useState(false);
  const [editHospitalError, setEditHospitalError] = React.useState("");
  const [editHospitalLoading, setEditHospitalLoading] = React.useState(false);

  const [employeeSearch, setEmployeeSearch] = React.useState("");
  const [showInactiveEmployees, setShowInactiveEmployees] = React.useState(false);
  const [editingEmployee, setEditingEmployee] = React.useState(null);
  const [editEmployeeForm, setEditEmployeeForm] = React.useState({ firstName: "", lastName: "", emailAddress: "", isActive: true });
  const [editEmployeeError, setEditEmployeeError] = React.useState("");
  const [editEmployeeLoading, setEditEmployeeLoading] = React.useState(false);

  const hospitalMenuButtonRefs = React.useRef({});
  const employeeMenuButtonRefs = React.useRef({});
  const reviewPanelRef = React.useRef(null);
  const preEditStateRef = React.useRef(null);

  const currentWeekStart = React.useMemo(() => {
    const baseWeekStart = parseLocalDate(getDefaultWeekRange()[0]) ?? new Date();
    return addWeeks(baseWeekStart, currentWeekOffset);
  }, [currentWeekOffset]);

  const currentWeekEnd = React.useMemo(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + 6);
    return endDate;
  }, [currentWeekStart]);

  const selectedWeekStartDate = React.useMemo(
    () => formatLocalDate(currentWeekStart),
    [currentWeekStart]
  );

  const selectedWeekEndDate = React.useMemo(
    () => formatLocalDate(currentWeekEnd),
    [currentWeekEnd]
  );

  const selectedWeekRange = React.useMemo(
    () => formatWeekRange(currentWeekStart),
    [currentWeekStart]
  );

  const reviewItems = React.useMemo(
    () => buildReviewItems(contractors, timecards),
    [contractors, timecards]
  );

  const reviewCounts = React.useMemo(
    () => buildStatusCounts(reviewItems),
    [reviewItems]
  );

  const hospitalsThisWeek = React.useMemo(() => {
    const statsMap = new Map();
    for (const item of reviewItems) {
      for (const name of item.hospitalNames) {
        if (!statsMap.has(name)) {
          statsMap.set(name, { total: 0, submitted: 0, draft: 0, approved: 0 });
        }
        const stats = statsMap.get(name);
        stats.total += 1;
        if (item.status === "submitted") stats.submitted += 1;
        else if (item.status === "draft") stats.draft += 1;
        else if (item.status === "approved") stats.approved += 1;
      }
    }
    return hospitalOptions
      .filter((h) => statsMap.has(h.name))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((h) => ({ ...h, stats: statsMap.get(h.name) }));
  }, [reviewItems, hospitalOptions]);

  const visibleReviewItems = React.useMemo(() => {
    let items = filterStatus === "all"
      ? reviewItems
      : reviewItems.filter((reviewItem) => reviewItem.status === filterStatus);

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      items = items.filter(
        (reviewItem) =>
          reviewItem.contractorName.toLowerCase().includes(q) ||
          reviewItem.emailAddress.toLowerCase().includes(q) ||
          reviewItem.loginUsername.toLowerCase().includes(q)
      );
    }

    return items;
  }, [filterStatus, reviewItems, searchQuery]);

  const selectedReviewItem = React.useMemo(
    () =>
      visibleReviewItems.find(
        (reviewItem) => reviewItem.contractorId === selectedContractorId
      ) ?? null,
    [selectedContractorId, visibleReviewItems]
  );

  React.useEffect(() => {
    let isMounted = true;

    async function loadDashboardData() {
      setDashboardLoading(true);
      setReviewLoadError("");

      try {
        const [hospitals, loadedContractors, loadedTimecards] = await Promise.all([
          fetchHospitalOptions(),
          fetchAdminContractors(),
          fetchAdminTimecards({ weekStartDate: selectedWeekStartDate })
        ]);

        if (!isMounted) {
          return;
        }

        setHospitalOptions(hospitals);
        setContractors(loadedContractors);
        setTimecards(loadedTimecards);
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setReviewLoadError(error?.message ?? "Unable to load admin dashboard data.");
        }
      } finally {
        if (isMounted) {
          setDashboardLoading(false);
        }
      }
    }

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, [refreshKey, selectedWeekStartDate]);

  React.useEffect(() => {
    setSelectedContractorId((currentValue) => {
      if (visibleReviewItems.some((reviewItem) => reviewItem.contractorId === currentValue)) {
        return currentValue;
      }

      return visibleReviewItems[0]?.contractorId ?? 0;
    });
  }, [visibleReviewItems]);

  React.useEffect(() => {
    let isMounted = true;

    async function loadTimecardDetail() {
      if (!selectedReviewItem || hospitalOptions.length === 0) {
        const rows = createEmptyRows();
        setDetailLoading(false);
        setSelectedTimecard(null);
        setResubmissions([]);
        setRegularRows(rows.regularRows);
        setCallRows(rows.callRows);
        setCallbackRows(rows.callbackRows);
        setWeekStartDate(selectedWeekStartDate);
        return;
      }

      if (!selectedReviewItem.timecardId) {
        const rows = createEmptyRows();
        setDetailLoading(false);
        setSelectedTimecard(null);
        setResubmissions([]);
        setRegularRows(rows.regularRows);
        setCallRows(rows.callRows);
        setCallbackRows(rows.callbackRows);
        setWeekStartDate(selectedWeekStartDate);
        return;
      }

      setDetailLoading(true);
      // Clear any prior timecard's history up front so it can never show under a
      // different contractor — including if the load below throws (the history block
      // renders outside the selectedTimecard guard).
      setResubmissions([]);

      try {
        const detail = await fetchTimecard(selectedReviewItem.timecardId);
        if (!detail || !isMounted) {
          if (isMounted) {
            const rows = createEmptyRows();
            setSelectedTimecard(null);
            setRegularRows(rows.regularRows);
            setCallRows(rows.callRows);
            setCallbackRows(rows.callbackRows);
            setWeekStartDate(selectedWeekStartDate);
          }
          return;
        }

        const formState = mapTimecardToFormState(detail, hospitalOptions);
        setSelectedTimecard({
          ...detail,
          contractorFirstName: selectedReviewItem.firstName,
          contractorLastName: selectedReviewItem.lastName,
          contractorEmailAddress: selectedReviewItem.emailAddress,
          contractorLoginUsername: selectedReviewItem.loginUsername
        });
        setRegularRows(formState.regularRows);
        setCallRows(formState.callRows);
        setCallbackRows(formState.callbackRows);
        setWeekStartDate(formState.weekStartDate || selectedWeekStartDate);

        // The timecard itself is ready — let the panel render now so a slow history
        // fetch doesn't hold it up.
        if (isMounted) {
          setDetailLoading(false);
        }

        // Load the resubmission audit trail separately so a failure here degrades to
        // "no history" rather than breaking the whole detail view.
        try {
          const history = await fetchResubmissions(selectedReviewItem.timecardId);
          if (isMounted) {
            setResubmissions(history);
          }
        } catch (historyError) {
          console.error(historyError);
          if (isMounted) {
            setResubmissions([]);
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setReviewLoadError(error?.message ?? "Unable to load the selected timecard.");
        }
      } finally {
        if (isMounted) {
          setDetailLoading(false);
        }
      }
    }

    loadTimecardDetail();

    return () => {
      isMounted = false;
    };
  }, [hospitalOptions, selectedReviewItem, selectedWeekStartDate]);

  React.useEffect(() => {
    setIsEditing(false);
    preEditStateRef.current = null;
  }, [selectedContractorId, selectedWeekStartDate, filterStatus]);

  React.useEffect(() => {
    if (!openMenuHospitalId) return;

    function handleOutsideClick(event) {
      const button = hospitalMenuButtonRefs.current[openMenuHospitalId];
      if (button?.closest(".row-menu-wrapper")?.contains(event.target)) return;
      setOpenMenuHospitalId(null);
    }

    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        setOpenMenuHospitalId(null);
        hospitalMenuButtonRefs.current[openMenuHospitalId]?.focus();
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [openMenuHospitalId]);

  const handleApprove = async () => {
    if (!selectedReviewItem?.timecardId) {
      return;
    }

    setApproveLoading(true);
    setReviewLoadError("");

    try {
      await approveTimecard(selectedReviewItem.timecardId);
      showToast(`${selectedReviewItem.contractorName}'s timecard was approved.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      console.error(error);
      setReviewLoadError(error?.message ?? "Unable to approve this timecard.");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedReviewItem?.timecardId) {
      return;
    }

    setExportLoading(true);
    setReviewLoadError("");

    try {
      const exportFile = await downloadTimecardExport(selectedReviewItem.timecardId);
      const objectUrl = URL.createObjectURL(exportFile.blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = exportFile.fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

      showToast(`${selectedReviewItem.contractorName}'s export downloaded successfully.`);
    } catch (error) {
      console.error(error);
      setReviewLoadError(error?.message ?? "Unable to export this timecard.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportHospitalPdf = async (hospitalId, hospitalName) => {
    setHospitalExportLoading((prev) => ({ ...prev, [hospitalId]: true }));
    setReviewLoadError("");

    try {
      const exportFile = await downloadHospitalCombinedExport(hospitalId, selectedWeekStartDate);
      const objectUrl = URL.createObjectURL(exportFile.blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = exportFile.fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      showToast(`${hospitalName} combined PDF downloaded.`);
    } catch (error) {
      console.error(error);
      setReviewLoadError(error?.message ?? `Unable to export timecards for ${hospitalName}.`);
    } finally {
      setHospitalExportLoading((prev) => ({ ...prev, [hospitalId]: false }));
    }
  };

  const handleCreateHospital = async (event) => {
    event.preventDefault();
    setHospitalError("");
    setIsCreatingHospital(true);

    try {
      const createdHospital = await createHospital({
        name: newHospitalName.trim(),
        departmentNames: []
      });
      setHospitalOptions((currentValue) =>
        [...currentValue, createdHospital].sort((left, right) => left.name.localeCompare(right.name))
      );
      showToast("Hospital added.");
      setNewHospitalName("");
    } catch (error) {
      console.error(error);
      setHospitalError(error?.message ?? "Unable to add the hospital.");
    } finally {
      setIsCreatingHospital(false);
    }
  };

  const handleCreateEmployee = async (event) => {
    event.preventDefault();
    setEmployeeError("");
    setIsCreatingEmployee(true);

    try {
      const createdEmployee = await createAdminContractor(newEmployee);
      setContractors((currentValue) =>
        [...currentValue, createdEmployee].sort((left, right) => {
          const lastNameComparison = left.lastName.localeCompare(right.lastName);
          return lastNameComparison !== 0
            ? lastNameComparison
            : left.firstName.localeCompare(right.firstName);
        })
      );
      showToast("Employee account created.");
      setNewEmployee({
        firstName: "",
        lastName: "",
        emailAddress: "",
        isActive: true
      });
    } catch (error) {
      console.error(error);
      setEmployeeError(error?.message ?? "Unable to create the employee account.");
    } finally {
      setIsCreatingEmployee(false);
    }
  };

  const handleOpenHospitalEdit = (hospital) => {
    setOpenMenuHospitalId(null);
    if (editingHospital?.hospitalId === hospital.hospitalId) {
      handleCloseHospitalEdit();
      return;
    }
    setEditingHospital(hospital);
    setEditHospitalName(hospital.name);
    setEditHospitalDepartmentName("");
    setEditHospitalDepartments(
      [...(hospital.departments ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((d) => ({ departmentId: d.departmentId, name: d.name }))
    );
    setEditHospitalExactTime(hospital.useExactShiftTimes ?? false);
    setEditHospitalError("");
  };

  const handleCloseHospitalEdit = () => {
    setEditingHospital(null);
    setEditHospitalName("");
    setEditHospitalDepartmentName("");
    setEditHospitalDepartments([]);
    setEditHospitalExactTime(false);
    setEditHospitalError("");
  };

  const handleSaveHospital = async (event) => {
    event.preventDefault();
    if (!editingHospital) return;
    setEditHospitalError("");
    setEditHospitalLoading(true);

    try {
      const updated = await updateHospital(editingHospital.hospitalId, {
        name: editHospitalName.trim(),
        useExactShiftTimes: editHospitalExactTime,
        departments: editHospitalDepartments.map((d) => ({ departmentId: d.departmentId || null, name: d.name }))
      });
      const normalizedUpdated = normalizeHospitalOption(updated);
      setHospitalOptions((current) =>
        current
          .map((h) => h.hospitalId === normalizedUpdated.hospitalId ? normalizedUpdated : h)
          .sort((left, right) => left.name.localeCompare(right.name))
      );
      handleCloseHospitalEdit();
    } catch (error) {
      console.error(error);
      setEditHospitalError(error?.message ?? "Unable to save changes.");
    } finally {
      setEditHospitalLoading(false);
    }
  };

  const handleAddEditHospitalDepartment = () => {
    const trimmedName = editHospitalDepartmentName.trim();
    if (!trimmedName) return;
    if (editHospitalDepartments.some((d) => d.name.toLowerCase() === trimmedName.toLowerCase())) return;
    setEditHospitalDepartments((current) =>
      [...current, { departmentId: 0, name: trimmedName }].sort((a, b) => a.name.localeCompare(b.name))
    );
    setEditHospitalDepartmentName("");
  };

  const handleRemoveEditHospitalDepartment = (departmentName) => {
    setEditHospitalDepartments((current) => current.filter((d) => d.name !== departmentName));
  };

  const handleRenameEditHospitalDepartment = (oldName, newName) => {
    setEditHospitalDepartments((current) => {
      const others = current.filter((d) => d.name.toLowerCase() !== oldName.toLowerCase());
      if (others.some((d) => d.name.toLowerCase() === newName.toLowerCase())) return current;
      return [...current.map((d) => d.name === oldName ? { ...d, name: newName } : d)]
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const handleOpenEmployeeEdit = (contractor) => {
    if (editingEmployee?.contractorId === contractor.contractorId) {
      handleCloseEmployeeEdit();
      return;
    }
    setEditingEmployee(contractor);
    setEditEmployeeForm({
      firstName: contractor.firstName ?? "",
      lastName: contractor.lastName ?? "",
      emailAddress: contractor.emailAddress ?? "",
      isActive: contractor.isActive ?? true
    });
    setEditEmployeeError("");
  };

  const handleCloseEmployeeEdit = () => {
    const contractorId = editingEmployee?.contractorId;
    setEditingEmployee(null);
    setEditEmployeeError("");
    if (contractorId) {
      window.setTimeout(() => {
        employeeMenuButtonRefs.current[contractorId]?.focus();
      }, 0);
    }
  };

  const handleSaveEmployee = async (event) => {
    event.preventDefault();
    if (!editingEmployee) return;
    setEditEmployeeError("");
    setEditEmployeeLoading(true);

    try {
      const updated = await updateAdminContractor(editingEmployee.contractorId, {
        firstName: editEmployeeForm.firstName.trim(),
        lastName: editEmployeeForm.lastName.trim(),
        emailAddress: editEmployeeForm.emailAddress.trim() || null,
        isActive: editEmployeeForm.isActive
      });
      setContractors((current) =>
        current.map((c) => c.contractorId === updated.contractorId ? updated : c)
      );
      handleCloseEmployeeEdit();
    } catch (error) {
      console.error(error);
      setEditEmployeeError(error?.message ?? "Unable to save changes.");
    } finally {
      setEditEmployeeLoading(false);
    }
  };

  const navigateWeek = React.useCallback((direction) => {
    setCurrentWeekOffset((previousOffset) => previousOffset + direction);
  }, []);

  const goToCurrentWeek = React.useCallback(() => {
    setCurrentWeekOffset(0);
  }, []);

  const handleSelectContractor = React.useCallback((contractorId) => {
    setSelectedContractorId(contractorId);
    reviewPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleAdminRegularRowChange = React.useCallback((rowIndex, field, value) => {
    setRegularRows((prev) => applyRegularRowChange(prev, rowIndex, field, value, hospitalOptions));
  }, [hospitalOptions]);

  const handleAdminCallRowChange = React.useCallback((rowIndex, field, value) => {
    setCallRows((prev) => prev.map((row, i) => {
      if (i !== rowIndex) {
        return row;
      }

      if (field === "hospital") {
        const updatedRow = {
          ...row,
          hospital: value,
          department: isDepartmentValidForHospital(hospitalOptions, value, row.department)
            ? row.department
            : ""
        };
        const cbRow = callbackRows.find(cb => cb.day === updatedRow.day);
        const cbHours = cbRow ? calculateCallBackTotalHours(cbRow, value, hospitalOptions) : "";
        return { ...updatedRow, totalHours: getNetCallHours(calculateCallTotalHours(updatedRow, hospitalOptions), cbHours) };
      }

      if (field === "startTime" || field === "endTime") {
        const updatedRow = { ...row, [field]: value };
        const cbRow = callbackRows.find(cb => cb.day === updatedRow.day);
        return { ...updatedRow, totalHours: getNetCallHours(calculateCallTotalHours(updatedRow, hospitalOptions), cbRow?.totalHours ?? "") };
      }

      return { ...row, [field]: value };
    }));
  }, [callbackRows, hospitalOptions]);

  const handleAdminCallbackRowChange = React.useCallback((rowIndex, field, value) => {
    const { callbackRows: nextCallbackRows, callRows: nextCallRows } =
      applyCallBackRowChange(callbackRows, callRows, rowIndex, field, value, hospitalOptions);
    setCallbackRows(nextCallbackRows);
    if (nextCallRows !== callRows) {
      setCallRows(nextCallRows);
    }
  }, [callbackRows, callRows, hospitalOptions]);

  const handleStartEdit = React.useCallback(() => {
    preEditStateRef.current = { regularRows, callRows, callbackRows };
    setReviewLoadError("");
    setIsEditing(true);
  }, [regularRows, callRows, callbackRows]);

  const handleCancelEdit = React.useCallback(() => {
    if (preEditStateRef.current) {
      setRegularRows(preEditStateRef.current.regularRows);
      setCallRows(preEditStateRef.current.callRows);
      setCallbackRows(preEditStateRef.current.callbackRows);
      preEditStateRef.current = null;
    }
    setIsEditing(false);
  }, []);

  const handleSaveEdit = async () => {
    if (!selectedReviewItem) return;

    // A "none" row has no timecard yet — admin is creating one on the contractor's behalf.
    const isNew = !selectedTimecard?.timecardId;
    if (!isNew && !selectedTimecard) return;

    setSaveLoading(true);
    setReviewLoadError("");

    try {
      const payload = buildTimecardPayload({
        timecardId: selectedTimecard?.timecardId ?? 0,
        contractorId: selectedTimecard?.contractorId ?? selectedReviewItem.contractorId,
        weekStartDate,
        weekEndDate: selectedTimecard?.weekEndDate ?? selectedWeekEndDate,
        status: selectedTimecard?.status ?? "draft",
        attestationAccepted: selectedTimecard?.attestationAccepted ?? false,
        signedByName: selectedTimecard?.signedByName ?? null,
        signatureValue: selectedTimecard?.signatureValue ?? null,
        signedDate: selectedTimecard?.signedDate ?? null,
        regularRows,
        callRows,
        callbackRows,
        hospitalOptions
      });

      if (isNew) {
        await adminCreateTimecard(payload);
        showToast(`Created a timecard for ${selectedReviewItem.contractorName}.`);
      } else {
        await adminUpdateTimecard(payload.timecardId, payload);
        showToast(`${selectedReviewItem.contractorName}'s timecard was updated.`);
      }
      preEditStateRef.current = null;
      setIsEditing(false);
      setRefreshKey((v) => v + 1);
    } catch (error) {
      console.error(error);
      setReviewLoadError(error?.message ?? "Unable to save the timecard.");
    } finally {
      setSaveLoading(false);
    }
  };

  const selectedTimecardStatus = selectedReviewItem?.status ?? "draft";
  const isCreatingNew = !!selectedReviewItem && !selectedReviewItem.timecardId;
  const canApprove = selectedTimecardStatus === "submitted" && !!selectedReviewItem?.timecardId;
  const filteredHospitals = hospitalSearch
    ? hospitalOptions.filter((h) => h.name.toLowerCase().includes(hospitalSearch.toLowerCase()))
    : hospitalOptions;

  const filteredEmployees = React.useMemo(() => {
    let list = showInactiveEmployees ? contractors : contractors.filter((c) => c.isActive);
    const q = employeeSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.toLowerCase();
        return name.includes(q) || (c.emailAddress ?? "").toLowerCase().includes(q) || (c.loginUsername ?? "").toLowerCase().includes(q);
      });
    }
    return list;
  }, [contractors, employeeSearch, showInactiveEmployees]);

  return (
    <div className="timecard-page">
      <AppHeader
        // title="Admin review"
        // subtitle="Review each employee's week, approve completed timecards, and manage hospitals and employee accounts."
        session={session}
        onLogout={onLogout}
        logoUrl={logoUrl}
      />

      <AdminTabs
        activeTab={activeAdminTab}
        onChange={setActiveAdminTab}
        counts={{
          review: reviewCounts.all,
          hospitals: hospitalOptions.length,
          employees: contractors.length
        }}
      />

      <main className="admin-workspace">
        {reviewLoadError ? (
          <div className="status-message error" role="alert">
            {reviewLoadError}
          </div>
        ) : null}

        {activeAdminTab === "review" && (
          <>
            <section className="sheet-section admin-queue" aria-label="Employee review queue">
              <div className="week-navigator">
                <button
                  type="button"
                  className="week-nav-button"
                  onClick={() => navigateWeek(-1)}
                  aria-label="Previous review week"
                >
                  {"‹"}
                </button>

                <div className="week-display">
                  <h3 className="week-range">{selectedWeekRange}</h3>
                  {currentWeekOffset !== 0 && (
                    <button
                      type="button"
                      className="week-current-button"
                      onClick={goToCurrentWeek}
                    >
                      Go to current week
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="week-nav-button"
                  onClick={() => navigateWeek(1)}
                  aria-label="Next review week"
                >
                  {"›"}
                </button>
              </div>

              <div className="roster-toolbar">
                <div className="roster-toolbar-bottom">
                  <input
                    type="search"
                    className="roster-search-input"
                    placeholder="Search employees…"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    aria-label="Search employees by name or email"
                  />
                  <div className="roster-filter-pills" role="group" aria-label="Filter by status">
                    {[
                      { value: "all", label: "All", count: reviewCounts.all },
                      { value: "submitted", label: "Submitted", count: reviewCounts.submitted },
                      { value: "draft", label: "Draft", count: reviewCounts.draft },
                      { value: "approved", label: "Approved", count: reviewCounts.approved },
                      { value: "none", label: "Not started", count: reviewCounts.none }
                    ].map(({ value, label, count }) => (
                      <button
                        key={value}
                        type="button"
                        className={`filter-pill${filterStatus === value ? " active" : ""}${value !== "all" ? ` filter-pill-${value}` : ""}`}
                        onClick={() => setFilterStatus(value)}
                      >
                        {label}
                        <span className="filter-pill-count">{count}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="refresh-action"
                    onClick={() => setRefreshKey((value) => value + 1)}
                    aria-label="Refresh week data"
                  >
                    ↻ Refresh
                  </button>
                </div>
              </div>

              <div className="review-queue-table">
                <div className="review-queue-header" aria-hidden="true">
                  <div className="rq-header-cell">Employee</div>
                  <div className="rq-header-cell">Hospital(s)</div>
                  <div className="rq-header-cell">Last Updated</div>
                  <div className="rq-header-cell">Emailed</div>
                  <div className="rq-header-cell">Status</div>
                </div>

                {visibleReviewItems.map((reviewItem) => {
                  const isSelected = selectedContractorId === reviewItem.contractorId;
                  const lastUpdated = formatRelativeTime(reviewItem.updatedUtc);
                  const hospitalSummary = formatHospitalSummary(reviewItem.primaryHospitalName, reviewItem.hospitalCount);
                  const hospitalNames = reviewItem.hospitalNames;
                  // Only meaningful once submitted: shows when the admin email went
                  // out, or flags a submission whose email never succeeded.
                  const emailedNotSent = Boolean(reviewItem.submittedUtc) && !reviewItem.emailedUtc;
                  const emailedLabel = reviewItem.submittedUtc
                    ? (reviewItem.emailedUtc ? formatRelativeTime(reviewItem.emailedUtc) : "Not sent")
                    : "—";
                  return (
                    <button
                      key={reviewItem.contractorId}
                      type="button"
                      className={`review-queue-row${isSelected ? " selected" : ""}`}
                      onClick={() => handleSelectContractor(reviewItem.contractorId)}
                      aria-pressed={isSelected}
                    >
                      <div className="review-queue-row-inner">
                        <div className="rq-col-employee">
                          <span className="rq-name">{reviewItem.contractorName}</span>
                          <span className="rq-email">
                            {reviewItem.emailAddress || reviewItem.loginUsername || "No login email"}
                          </span>
                        </div>
                        <HospitalSummaryCell summary={hospitalSummary} hospitalNames={hospitalNames} />
                        <div className="rq-col-lastupdated">{lastUpdated}</div>
                        <div className={`rq-col-emailed${emailedNotSent ? " rq-emailed-warning" : ""}`}>
                          {emailedLabel}
                        </div>
                        <div className="rq-col-status">
                          <span className={`status-pill status-pill-${reviewItem.status}`}>
                            {formatStatusLabel(reviewItem.status)}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {!dashboardLoading && !visibleReviewItems.length ? (
                  <div className="empty-state">
                    No employees match the current filter for this week.
                  </div>
                ) : null}
              </div>
            </section>

            <section ref={reviewPanelRef} className="sheet-section admin-review-panel" aria-label="Selected timecard review">

              {detailLoading ? <div className="inline-status">Loading timecard details...</div> : null}

              {!detailLoading && selectedReviewItem ? (
                <>
                  <div className="tc-summary-bar">
                    <div className="tc-summary-row-1">
                      <span className="tc-summary-name">{selectedReviewItem.contractorName}</span>
                      <span className={`status-pill status-pill-${selectedTimecardStatus}`}>
                        {formatStatusLabel(selectedTimecardStatus)}
                      </span>
                    </div>
                    <div className="tc-summary-row-2">
                      {formatDisplayDate(selectedWeekStartDate)} – {formatDisplayDate(selectedWeekEndDate)}
                      <span className="tc-meta-sep">·</span>
                      {selectedTimecard?.signedByName ? `Signed by ${selectedTimecard.signedByName}` : "Not signed"}
                    </div>
                    <div className="action-row admin-review-button-row tc-summary-actions">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="secondary-action"
                            onClick={handleCancelEdit}
                            disabled={saveLoading}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="primary-action"
                            onClick={handleSaveEdit}
                            disabled={saveLoading}
                          >
                            {isCreatingNew
                              ? (saveLoading ? "Creating…" : "Create timecard")
                              : (saveLoading ? "Saving…" : "Save changes")}
                          </button>
                        </>
                      ) : isCreatingNew ? (
                        <button
                          type="button"
                          className="primary-action"
                          onClick={handleStartEdit}
                        >
                          Create timecard
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="secondary-action"
                            onClick={handleStartEdit}
                            disabled={!selectedTimecard}
                          >
                            Edit timecard
                          </button>
                          <button
                            type="button"
                            className="secondary-action"
                            onClick={handleExportPdf}
                            disabled={exportLoading}
                          >
                            {exportLoading ? "Exporting…" : "Export PDF(s)"}
                          </button>
                          {canApprove ? (
                            <button
                              type="button"
                              className="primary-action"
                              onClick={handleApprove}
                              disabled={approveLoading}
                            >
                              {approveLoading ? "Approving…" : "Approve timecard"}
                            </button>
                          ) : selectedTimecardStatus === "approved" ? (
                            <span className="status-pill status-pill-approved">Already approved</span>
                          ) : selectedTimecardStatus === "submitted" ? (
                            <span className="status-pill status-pill-submitted">Ready for approval</span>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>

                  {resubmissions.length > 0 ? (
                    <div className="tc-resubmission-history">
                      <h4 className="tc-resubmission-heading">Resubmission history</h4>
                      <ul className="tc-resubmission-list">
                        {resubmissions.map((entry) => (
                          <li key={entry.resubmissionId} className="tc-resubmission-item">
                            <span className="tc-resubmission-when">
                              {formatRelativeTime(entry.createdUtc)}
                            </span>
                            <span className="tc-resubmission-reason">{entry.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {selectedTimecard || isCreatingNew ? (
                    <TimecardEntryArea
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      regularRows={regularRows}
                      callRows={callRows}
                      callbackRows={callbackRows}
                      onRegularRowChange={isEditing ? handleAdminRegularRowChange : () => {}}
                      onCallRowChange={isEditing ? handleAdminCallRowChange : () => {}}
                      onCallBackRowChange={isEditing ? handleAdminCallbackRowChange : () => {}}
                      hospitalOptions={hospitalOptions}
                      weekStartDate={weekStartDate}
                      onClockAction={undefined}
                      readOnly={!isEditing}
                    />
                  ) : (
                    <div className="empty-state">
                      Unable to load the selected timecard for {selectedReviewItem.contractorName}.
                    </div>
                  )}
                </>
              ) : null}

              {!detailLoading && !selectedReviewItem ? (
                <div className="empty-state">
                  Select an employee from the roster to review this week.
                </div>
              ) : null}
            </section>

            {!dashboardLoading && hospitalsThisWeek.length > 0 && (
              <section className="sheet-section admin-hospital-summary" aria-label="Hospitals active this week">
                <h3 className="admin-hospital-summary-heading">Hospitals this week</h3>
                <ul className="hospital-summary-list">
                  {hospitalsThisWeek.map((hospital) => (
                    <li key={hospital.hospitalId} className="hospital-summary-row">
                      <div className="hospital-summary-info">
                        <span className="hospital-summary-name">{hospital.name}</span>
                        <span className="hospital-summary-meta">
                          {hospital.stats.total} {hospital.stats.total === 1 ? "employee" : "employees"}
                          {hospital.stats.submitted > 0 && <> · <span className="hss-submitted">{hospital.stats.submitted} submitted</span></>}
                          {hospital.stats.draft > 0 && <> · <span className="hss-draft">{hospital.stats.draft} draft</span></>}
                          {hospital.stats.approved > 0 && <> · <span className="hss-approved">{hospital.stats.approved} approved</span></>}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="secondary-action hospital-summary-download-btn"
                        onClick={() => handleExportHospitalPdf(hospital.hospitalId, hospital.name)}
                        disabled={!!hospitalExportLoading[hospital.hospitalId]}
                      >
                        {hospitalExportLoading[hospital.hospitalId] ? "Exporting…" : "Download PDF"}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {activeAdminTab === "hospitals" && (
          <section className="sheet-section" aria-labelledby="admin-hospital-title">
            <SectionHeading title="Hospitals" titleId="admin-hospital-title" />

            <div className="hospital-add-panel">
              <h3 className="config-subheading">Add new hospital</h3>

              <form className="hospital-form" onSubmit={handleCreateHospital}>
                <Field id="hospital-name" label="Hospital name">
                  <input
                    id="hospital-name"
                    type="text"
                    value={newHospitalName}
                    onChange={(event) => setNewHospitalName(event.target.value)}
                    disabled={isCreatingHospital}
                  />
                </Field>

                <button type="submit" className="primary-action create-action" disabled={isCreatingHospital}>
                  {isCreatingHospital ? "Adding…" : "+ Add hospital"}
                </button>
              </form>

              {hospitalError ? (
                <div className="status-message error" role="alert">
                  {hospitalError}
                </div>
              ) : null}
            </div>

            <div className="config-list-section">
              <div className="config-list-header">
                <h3 className="config-subheading">Existing hospitals</h3>
                <input
                  type="search"
                  className="hospital-search-input"
                  placeholder="Search hospitals…"
                  value={hospitalSearch}
                  onChange={(event) => setHospitalSearch(event.target.value)}
                />
              </div>

              <div className="hospital-admin-table">
                <div className="hospital-admin-header" aria-hidden="true">
                  <div>Hospital</div>
                  <div>Departments</div>
                  <div></div>
                </div>
                <div className="hospital-admin-body">
                  {filteredHospitals.map((hospital) => {
                    const isExpanded = editingHospital?.hospitalId === hospital.hospitalId;
                    return (
                      <div key={hospital.hospitalId} className={`hospital-admin-row-group${isExpanded ? " is-expanded" : ""}`}>
                        <div
                          className="hospital-admin-row"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? `Collapse editor for ${hospital.name}` : `Edit ${hospital.name}`}
                          ref={(el) => { hospitalMenuButtonRefs.current[hospital.hospitalId] = el; }}
                          onClick={() => handleOpenHospitalEdit(hospital)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpenHospitalEdit(hospital); } }}
                        >
                          <div className="ha-col-name">
                            <strong>{hospital.name}</strong>
                          </div>
                          <div className="ha-col-departments">
                            {(hospital.departments?.length ?? 0) > 0 ? (
                              <div className="department-summary">
                                {hospital.departments.map((department) => (
                                  <span key={department.departmentId ?? department.name} className="department-pill">
                                    {department.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="department-summary-empty">No departments</span>
                            )}
                          </div>
                          <div className="ha-col-actions">
                            <span className={`status-pill status-pill-sm ${hospital.isActive ? "status-pill-approved" : "status-pill-draft"}`}>
                              {hospital.isActive ? "Active" : "Inactive"}
                            </span>
                            <svg className={`ha-chevron${isExpanded ? " ha-chevron--open" : ""}`} aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 6l6 6-6 6"/>
                            </svg>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="hospital-inline-editor">
                            <form onSubmit={handleSaveHospital}>
                              <div className="hospital-inline-editor-cols">
                                <div className="hospital-inline-col hospital-inline-col--details">
                                  <p className="hospital-inline-col-heading">Hospital details</p>
                                  <Field id={`edit-hospital-name-${hospital.hospitalId}`} label="Hospital name">
                                    <input
                                      id={`edit-hospital-name-${hospital.hospitalId}`}
                                      type="text"
                                      value={editHospitalName}
                                      onChange={(event) => setEditHospitalName(event.target.value)}
                                      disabled={editHospitalLoading}
                                      required
                                    />
                                  </Field>
                                  <label className="compact-check">
                                    <input
                                      type="checkbox"
                                      checked={editHospitalExactTime}
                                      onChange={(event) => setEditHospitalExactTime(event.target.checked)}
                                      disabled={editHospitalLoading}
                                    />
                                    <span>Use exact shift times</span>
                                  </label>
                                </div>
                                <div className="hospital-inline-col hospital-inline-col--depts">
                                  <p className="hospital-inline-col-heading">Departments</p>
                                  <DepartmentNameEditor
                                    inputId={`edit-dept-name-${hospital.hospitalId}`}
                                    value={editHospitalDepartmentName}
                                    onValueChange={setEditHospitalDepartmentName}
                                    departments={editHospitalDepartments.map((d) => d.name)}
                                    onAddDepartment={handleAddEditHospitalDepartment}
                                    onRemoveDepartment={handleRemoveEditHospitalDepartment}
                                    onRenameDepartment={handleRenameEditHospitalDepartment}
                                    disabled={editHospitalLoading}
                                    emptyMessage="No departments added yet."
                                  />
                                  {editHospitalError ? (
                                    <div className="status-message error" role="alert">{editHospitalError}</div>
                                  ) : null}
                                  <div className="hospital-inline-editor-footer">
                                    <button
                                      type="button"
                                      className="secondary-action"
                                      onClick={handleCloseHospitalEdit}
                                      disabled={editHospitalLoading}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="submit"
                                      className="primary-action create-action"
                                      disabled={editHospitalLoading || !editHospitalName.trim()}
                                    >
                                      {editHospitalLoading ? "Saving…" : "Save changes"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredHospitals.length === 0 && (
                    <div className="hospital-admin-empty">
                      {hospitalSearch ? "No hospitals match your search." : "No hospitals have been added yet."}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeAdminTab === "employees" && (
          <section className="sheet-section" aria-labelledby="admin-employee-title">
            <SectionHeading title="Employees" titleId="admin-employee-title" />

            <div className="employee-add-panel">
              <h3 className="config-subheading">Add new employee</h3>

              <form className="employee-form" onSubmit={handleCreateEmployee}>
                <Field id="employee-first-name" label="First name">
                  <input
                    id="employee-first-name"
                    type="text"
                    value={newEmployee.firstName}
                    onChange={(event) =>
                      setNewEmployee((currentValue) => ({
                        ...currentValue,
                        firstName: event.target.value
                      }))
                    }
                    disabled={isCreatingEmployee}
                  />
                </Field>

                <Field id="employee-last-name" label="Last name">
                  <input
                    id="employee-last-name"
                    type="text"
                    value={newEmployee.lastName}
                    onChange={(event) =>
                      setNewEmployee((currentValue) => ({
                        ...currentValue,
                        lastName: event.target.value
                      }))
                    }
                    disabled={isCreatingEmployee}
                  />
                </Field>

                <Field id="employee-email" label="Email" wide>
                  <input
                    id="employee-email"
                    type="email"
                    value={newEmployee.emailAddress}
                    onChange={(event) =>
                      setNewEmployee((currentValue) => ({
                        ...currentValue,
                        emailAddress: event.target.value
                      }))
                    }
                    disabled={isCreatingEmployee}
                  />
                </Field>

                <label className="compact-check" htmlFor="employee-is-active">
                  <input
                    id="employee-is-active"
                    type="checkbox"
                    checked={newEmployee.isActive}
                    onChange={(event) =>
                      setNewEmployee((currentValue) => ({
                        ...currentValue,
                        isActive: event.target.checked
                      }))
                    }
                    disabled={isCreatingEmployee}
                  />
                  <span>Active account</span>
                </label>

                <button type="submit" className="primary-action create-action" disabled={isCreatingEmployee}>
                  {isCreatingEmployee ? "Creating…" : "+ Add employee"}
                </button>
              </form>

              {employeeError ? (
                <div className="status-message error" role="alert">
                  {employeeError}
                </div>
              ) : null}
            </div>

            <div className="config-list-section">
              <div className="config-list-header">
                <h3 className="config-subheading">Existing employees</h3>
                <div className="config-list-header-controls">
                  <label className="employee-inactive-toggle">
                    <input
                      type="checkbox"
                      checked={showInactiveEmployees}
                      onChange={(e) => setShowInactiveEmployees(e.target.checked)}
                    />
                    <span>Show inactive employees</span>
                  </label>
                  <input
                    type="search"
                    className="employee-search-input"
                    placeholder="Search employees…"
                    value={employeeSearch}
                    onChange={(event) => setEmployeeSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="employee-admin-table">
                <div className="employee-admin-header" aria-hidden="true">
                  <div>Employee</div>
                  <div>Email</div>
                  <div>Last login</div>
                  <div>Status</div>
                  <div></div>
                </div>
                <div className="employee-admin-body">
                  {filteredEmployees.map((contractor) => {
                    const isExpanded = editingEmployee?.contractorId === contractor.contractorId;
                    return (
                      <div key={contractor.contractorId} className={`employee-admin-row-group${isExpanded ? " is-expanded" : ""}`}>
                        <div
                          className="employee-admin-row"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? `Collapse editor for ${contractor.firstName} ${contractor.lastName}` : `Edit ${contractor.firstName} ${contractor.lastName}`}
                          ref={(el) => { employeeMenuButtonRefs.current[contractor.contractorId] = el; }}
                          onClick={() => handleOpenEmployeeEdit(contractor)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpenEmployeeEdit(contractor); } }}
                        >
                          <div className="ea-col-name">
                            <strong>{contractor.firstName} {contractor.lastName}</strong>
                          </div>
                          <div className="ea-col-email">
                            {contractor.emailAddress || contractor.loginUsername || "No login email"}
                          </div>
                          <div className="ea-col-login">
                            {formatLastLogin(contractor.lastLoginUtc)}
                          </div>
                          <div className="ea-col-status">
                            <span className={`status-pill status-pill-sm ${contractor.isActive ? "status-pill-approved" : "status-pill-inactive"}`}>
                              {contractor.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="ea-col-actions">
                            <svg className={`ea-chevron${isExpanded ? " ea-chevron--open" : ""}`} aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 6l6 6-6 6"/>
                            </svg>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="employee-inline-editor">
                            <form onSubmit={handleSaveEmployee}>
                              <div className="employee-inline-editor-fields">
                                <Field id={`edit-emp-first-${contractor.contractorId}`} label="First name">
                                  <input
                                    id={`edit-emp-first-${contractor.contractorId}`}
                                    type="text"
                                    value={editEmployeeForm.firstName}
                                    onChange={(e) => setEditEmployeeForm((f) => ({ ...f, firstName: e.target.value }))}
                                    disabled={editEmployeeLoading}
                                    required
                                  />
                                </Field>
                                <Field id={`edit-emp-last-${contractor.contractorId}`} label="Last name">
                                  <input
                                    id={`edit-emp-last-${contractor.contractorId}`}
                                    type="text"
                                    value={editEmployeeForm.lastName}
                                    onChange={(e) => setEditEmployeeForm((f) => ({ ...f, lastName: e.target.value }))}
                                    disabled={editEmployeeLoading}
                                    required
                                  />
                                </Field>
                                <Field id={`edit-emp-email-${contractor.contractorId}`} label="Email" wide>
                                  <input
                                    id={`edit-emp-email-${contractor.contractorId}`}
                                    type="email"
                                    value={editEmployeeForm.emailAddress}
                                    onChange={(e) => setEditEmployeeForm((f) => ({ ...f, emailAddress: e.target.value }))}
                                    disabled={editEmployeeLoading}
                                  />
                                </Field>
                                <label className="compact-check">
                                  <input
                                    type="checkbox"
                                    checked={editEmployeeForm.isActive}
                                    onChange={(e) => setEditEmployeeForm((f) => ({ ...f, isActive: e.target.checked }))}
                                    disabled={editEmployeeLoading}
                                  />
                                  <span>Active account</span>
                                </label>
                              </div>
                              {editEmployeeError ? (
                                <div className="status-message error" role="alert">{editEmployeeError}</div>
                              ) : null}
                              <div className="employee-inline-editor-footer">
                                <button
                                  type="button"
                                  className="secondary-action"
                                  onClick={handleCloseEmployeeEdit}
                                  disabled={editEmployeeLoading}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  className="primary-action create-action"
                                  disabled={editEmployeeLoading || !editEmployeeForm.firstName.trim() || !editEmployeeForm.lastName.trim()}
                                >
                                  {editEmployeeLoading ? "Saving…" : "Save changes"}
                                </button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredEmployees.length === 0 && (
                    <div className="hospital-admin-empty">
                      {employeeSearch ? "No employees match your search." : "No employees have been added yet."}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Toast message={toastMessage} visible={toastVisible} onDismiss={dismissToast} />
    </div>
  );
}
